// The server plugins a reader INVOKES from a conversation — the ones the Mattermost webapp puts in
// its App Bar and channel header (`registerAppBarComponent` / `registerChannelHeaderButtonAction`):
// Google Meet (`com.satoritech.meet`) and the time clock (`com.satoritech.registry`,
// shared/PLUGINS.md §9). Pure rules only; the worker half is data/features/appPlugins.ts, the
// header actions ui/features/appPlugins/.
//
// Which plugins are ACTIVE: `GET /api/v4/plugins/webapp` — the list the webapp itself loads its
// plugin bundles from. It is the one plugin list a non-admin may read (`/plugins/statuses` is
// admin-only), and both plugins here ship a webapp bundle, so "listed" = "active" for them. The
// native clients ask nothing and always offer both actions; the web keeps exactly that default
// while the list is UNKNOWN (first launch, offline, a malformed answer) and hides an action only
// once the server has positively said its plugin is not running.

export const MEET_PLUGIN_ID = 'com.satoritech.meet';
export const REGISTRY_PLUGIN_ID = 'com.satoritech.registry';

/** The Meet plugin's default meeting length — the value every native client sends. */
export const MEET_DEFAULT_DURATION_MINUTES = 60;

/** `sync_state` key for the last plugin list the server gave (JSON array of ids). */
export const ACTIVE_PLUGINS_KEY = 'web_active_plugins';

/** The ids in a `/plugins/webapp` answer, sorted and unique; null when the answer is not a list. */
function parseActive(json: unknown): string[] | null {
  if (!Array.isArray(json)) return null;
  const ids = new Set<string>();
  for (const manifest of json) {
    const id = manifest && typeof manifest === 'object' ? (manifest as { id?: unknown }).id : null;
    if (typeof id === 'string' && id) ids.add(id);
  }
  return [...ids].sort();
}

/** What the store keeps; `decode` is its exact inverse (and null for anything else). */
function encode(ids: string[]): string {
  return JSON.stringify(ids);
}

function decode(stored: string | null): string[] | null {
  if (!stored) return null;
  try {
    const value = JSON.parse(stored);
    return Array.isArray(value) && value.every((v) => typeof v === 'string') ? value : null;
  } catch {
    return null;
  }
}

/** Offer the plugin's action? Unknown list → yes (the natives' behaviour); known → only if listed. */
function isOffered(active: readonly string[] | null, pluginId: string): boolean {
  return active === null || active.includes(pluginId);
}

/**
 * The `device` the registry records. The plugin's OWN webapp derives it from the user agent
 * (android / iphone / ipad / "mobile" → mobile; desktop OSes → desktop); the Mac sends "desktop",
 * iOS and Android "mobile". A browser is whichever of the two it runs on, so the web applies the
 * plugin's rule — a phone browser is not a desk — and, like the apps, never sends the plugin's
 * third value ("web"): the registry tells desks from phones, not one client from another.
 */
function registryDevice(userAgent: string): 'mobile' | 'desktop' {
  const ua = userAgent.toLowerCase();
  return ua.includes('android') || ua.includes('iphone') || ua.includes('ipad') || ua.includes('mobile') ? 'mobile' : 'desktop';
}

/** The body of `POST /meta` and `POST /hello` — exactly the apps' three fields (no IP: the plugin
 * reads the request's own address, which the proxy forwards). */
function registryBody(userId: string, channelId: string, device: string): { user_id: string; channel_id: string; device: string } {
  return { user_id: userId, channel_id: channelId, device };
}

/** The server's `default-title` answer, or the conversation's own name when it gave none. */
function meetTitle(defaultTitleJson: unknown, fallback: string): string {
  const raw = defaultTitleJson && typeof defaultTitleJson === 'object' ? (defaultTitleJson as { title?: unknown }).title : null;
  const title = typeof raw === 'string' ? raw.trim() : '';
  return title || fallback;
}

export const AppPlugins = { parseActive, encode, decode, isOffered, registryDevice, registryBody, meetTitle };
